import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import cardTexture from '../../assets/figma/card-texture.jpg';
import { useUiStore } from '../../store/ui';
import { dbgFlag } from '../../lib/debug';
import { MastercardLogo, SnowflakeIcon, VisaLogo } from './icons';

type Props = {
  last4: string;
  balance: string;
  expiry: string;
  /** User-defined card name, shown under the INCPT logo. */
  label?: string | null;
  /** Show Mastercard logo instead of Visa (templates 14/24/27). */
  mastercard?: boolean;
  frozen?: boolean;
  /** When true the card flips (Revolut-style) to reveal the details on the back. */
  flipped?: boolean;
  /** Full PAN, e.g. "4000 1234 5678 7381". */
  number?: string;
  cvv?: string;
  /** Card is being issued — hides number/balance, shows animated border stroke. */
  issuing?: boolean;
  /** ISO timestamp — used to show elapsed issuing time in the bottom-left badge. */
  createdAt?: string;
  /** Tap anywhere on the card (except copy button on the back) to flip / reveal details. */
  onFlip?: () => void;
  /** Copy full PAN — only passed once details are revealed (shown on the back). */
  onCopyNumber?: () => void;
  /**
   * Off-screen carousel neighbour: front face only, no 3D / flip / remount
   * listeners. Legacy imports can leave a user with many cards; mounting a
   * full preserve-3d CardLarge for each has crashed Telegram iOS WebViews
   * (same blank screen as the top-up keyboard path on fragile devices).
   */
  lite?: boolean;
};

const HIDDEN_BACKFACE: CSSProperties = {
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
};

/** Animated "comet" stroke that traces the card border while the card is issuing. */
function IssuingBorder() {
  return (
    <>
      {/* @property lets the conic-gradient angle animate smoothly without rotating the element */}
      <style>{`
        @property --_ci {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes _ci_spin {
          to { --_ci: 360deg; }
        }
        .card-issuing-border {
          animation: _ci_spin 1.6s linear infinite;
          background: conic-gradient(
            from var(--_ci),
            transparent 60%,
            rgba(255,255,255,0.45) 78%,
            rgba(255,255,255,0.95) 88%,
            rgba(255,255,255,0.95) 92%,
            rgba(255,255,255,0.4) 96%,
            transparent 100%
          );
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          padding: 3.5px;
          border-radius: 20px;
        }
      `}</style>
      <div className="card-issuing-border pointer-events-none absolute inset-0 z-10 rounded-[20px]" />
    </>
  );
}

function CardBg({ flat = false }: { flat?: boolean }) {
  // Carousel virtualization + safe-mode: off-screen (`flat`) cards and the
  // "card images" bisect flag both drop the 1313×800 texture (~4MB decoded,
  // ×N cards) for a cheap gradient. This caps decoded-image memory at the ~3
  // on-screen cards regardless of how many the user holds — the OOM that hard-
  // reloaded the iOS WebView after legacy imports pushed the count to 7+.
  const flatBg = flat || dbgFlag('noimg');
  return (
    <>
      {flatBg ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#eef1f6] to-[#d8dee7]"
        />
      ) : (
        <img
          src={cardTexture}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[20px] shadow-[inset_0px_0px_40px_0px_rgba(0,0,0,0.16)]"
      />
    </>
  );
}

/** Full-width holographic card. Flips to reveal number / expiry / CVV. */
export function CardLarge({
  last4,
  balance,
  expiry,
  label,
  mastercard,
  frozen,
  flipped,
  number,
  cvv,
  issuing,
  createdAt,
  onFlip,
  onCopyNumber,
  lite = false,
}: Props) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (lite || !issuing || !createdAt) return;
    const origin = new Date(createdAt).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - origin) / 1000));
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [lite, issuing, createdAt]);

  // iOS Telegram WebView drops the GPU layer backing the 3D flip when the mini
  // app is backgrounded, so on resume the card can repaint as its mirrored back
  // face. Remounting after a real background→foreground rebuilds those layers
  // (see also the `visibility` guard below, which keeps the stale frame from
  // ever reaching the screen). What we removed is remounting on every casual
  // `visibilitychange`/`pageshow` (those also fire around the soft keyboard and
  // were killing the WebView during top-up).
  const [repaintKey, setRepaintKey] = useState(0);
  const sheetOpen = useUiStore((s) => s.sheetCount > 0);
  useEffect(() => {
    if (lite) return;
    let wasBackgrounded = document.visibilityState === 'hidden';
    const remount = () => {
      // Never rebuild 3D layers while a sheet (e.g. Top up + keyboard) is open.
      if (sheetOpen) return;
      setRepaintKey((k) => k + 1);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasBackgrounded = true;
        return;
      }
      if (!wasBackgrounded) return;
      wasBackgrounded = false;
      remount();
    };
    // bfcache restore (swipe-back / frozen tab) — same "GPU layer lost" case.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) remount();
    };
    // Telegram Mini App activate/deactivate (when the client backgrounds us
    // without a reliable document.hidden in some iOS builds).
    const wa = window.Telegram?.WebApp;
    const onDeactivated = () => {
      wasBackgrounded = true;
    };
    const onActivated = () => {
      if (!wasBackgrounded) return;
      wasBackgrounded = false;
      remount();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    wa?.onEvent?.('deactivated', onDeactivated);
    wa?.onEvent?.('activated', onActivated);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      wa?.offEvent?.('deactivated', onDeactivated);
      wa?.offEvent?.('activated', onActivated);
    };
  }, [lite, sheetOpen]);

  // `backface-visibility` is a compositing-level hint and is exactly what the
  // WebView loses above, so it cannot be the only thing keeping the mirrored
  // back face off screen. Once a flip has settled we also hard-hide the face
  // that is turned away: `visibility` lives in the render tree and survives a
  // lost GPU layer. It can only be applied while the card is at rest — during
  // the 700ms rotation both faces must stay paintable.
  const [flipping, setFlipping] = useState(false);
  const prevFlipped = useRef(flipped);
  useEffect(() => {
    if (lite || prevFlipped.current === flipped) return;
    prevFlipped.current = flipped;
    setFlipping(true);
    const id = setTimeout(() => setFlipping(false), 750);
    return () => clearTimeout(id);
  }, [flipped, lite]);
  const faceVisibility = (isBack: boolean): CSSProperties['visibility'] =>
    !flipping && isBack !== !!flipped ? 'hidden' : 'visible';

  const mins = Math.floor(elapsed / 60);
  const issuingLabel =
    mins >= 1
      ? t('cards.issuingFor', { time: `${mins} ${t(mins === 1 ? 'cards.minute' : 'cards.minutes')}` })
      : t('cards.issuingNow');

  // Group the PAN in 4s, e.g. "4937 2800 6794 8933".
  const group4 = (n: string) => n.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
  const fullNumber = number ? group4(number) : `•••• •••• •••• ${last4}`;

  const cardName = label?.trim() || null;

  const frontFace = (
    <>
      <CardBg flat={lite} />
      <div className="relative flex h-full flex-col justify-between p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left">
            <span className="text-[26px] font-semibold leading-none text-ink">INCPT</span>
            {cardName && (
              <span className="max-w-full truncate text-[13px] font-medium leading-none text-ink/55">
                {cardName}
              </span>
            )}
          </div>
          {mastercard ? (
            <MastercardLogo className="h-[36px] w-[58px] shrink-0 object-contain" />
          ) : (
            <VisaLogo className="h-[36px] w-[108px] shrink-0 object-contain" />
          )}
        </div>
        {!issuing && !/^[•*]+$/.test(last4) ? (
          <div className="flex items-center gap-3 text-ink/55">
            <span className="text-[14px] leading-none tracking-[0.3em]">• • • • • • • •</span>
            <span className="text-[18px] leading-none tabular-nums">{last4}</span>
          </div>
        ) : (
          <div />
        )}
        <div className="flex items-end justify-between">
          {issuing ? (
            <span className="rounded-full bg-black/[0.08] px-3 py-1.5 text-[12px] font-medium text-ink/70">
              {issuingLabel}
            </span>
          ) : (
            <span className="min-h-[34px] text-[28px] font-medium leading-none tabular-nums text-ink">
              {balance}
            </span>
          )}
          {!issuing && (
            <span className="text-[12px] leading-none tabular-nums text-black/[0.46]">{expiry}</span>
          )}
        </div>
      </div>
      {issuing && <IssuingBorder />}
      <div
        aria-hidden={!frozen}
        className={`pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-[20px] bg-white/40 backdrop-blur-[3px] transition-opacity duration-500 ${
          frozen ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <SnowflakeIcon size={44} className="text-white drop-shadow-[0px_2px_6px_rgba(0,0,0,0.25)]" />
        <span className="text-[14px] font-semibold uppercase tracking-wide text-white drop-shadow-[0px_1px_4px_rgba(0,0,0,0.3)]">
          {t('cards.frozen')}
        </span>
      </div>
    </>
  );

  // Static front: off-screen neighbours (`lite`), and any card that can't flip.
  // A disabled <button> plus preserve-3d swallows the parent scroll on iOS.
  if (lite || !onFlip) {
    return (
      <div
        className={`relative aspect-[275/174] w-full overflow-hidden rounded-[20px] ${frozen ? 'saturate-[0.5]' : ''}`}
        aria-hidden={lite || undefined}
      >
        {frontFace}
      </div>
    );
  }

  return (
    <div className="aspect-[275/174] w-full" style={{ perspective: '1400px' }}>
      <div
        key={repaintKey}
        className={`relative h-full w-full transition-transform duration-700 [transform-style:preserve-3d] ${
          frozen ? 'saturate-[0.5]' : ''
        }`}
        style={{ transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
      >
        {/* ── FRONT ── */}
        <button
          type="button"
          onClick={!issuing && !frozen ? onFlip : undefined}
          disabled={issuing || frozen || !onFlip}
          className="absolute inset-0 overflow-hidden rounded-[20px] text-left active:opacity-90 disabled:pointer-events-none"
          style={{ ...HIDDEN_BACKFACE, visibility: faceVisibility(false) }}
          aria-label={t('cards.details')}
        >
          {frontFace}
        </button>

        {/* ── BACK (details) ── */}
        <button
          type="button"
          onClick={onFlip}
          disabled={!onFlip}
          className="absolute inset-0 overflow-hidden rounded-[20px] text-left disabled:pointer-events-none"
          style={{
            ...HIDDEN_BACKFACE,
            transform: 'rotateY(180deg)',
            visibility: faceVisibility(true),
          }}
          aria-label={t('cards.details')}
        >
          <CardBg />
          <div className="relative flex h-full flex-col justify-between p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left">
                <span className="text-[20px] font-semibold leading-none text-ink">INCPT</span>
                {cardName && (
                  <span className="max-w-full truncate text-[12px] font-medium leading-none text-ink/55">
                    {cardName}
                  </span>
                )}
              </div>
              {mastercard ? (
                <MastercardLogo className="h-[30px] w-[48px] shrink-0 object-contain" />
              ) : (
                <VisaLogo className="h-[30px] w-[90px] shrink-0 object-contain" />
              )}
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wide text-black/[0.46]">
                {t('cards.cardNumber')}
              </span>
              <div className="flex min-w-0 items-center gap-1.5 min-[380px]:gap-2">
                {/* Narrow phones: shrink size/tracking so the PAN stays on one line. */}
                <span className="whitespace-nowrap text-[15px] leading-none tracking-[0.04em] tabular-nums text-ink max-[340px]:text-[14px] max-[340px]:tracking-[0.02em] min-[360px]:text-[17px] min-[360px]:tracking-[0.06em] min-[400px]:text-[19px] min-[400px]:tracking-[0.08em] min-[430px]:text-[20px] min-[430px]:tracking-[0.12em]">
                  {fullNumber}
                </span>
                {onCopyNumber && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyNumber();
                    }}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/[0.08] text-ink/55 transition-opacity active:opacity-60"
                    aria-label={t('common.copy')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-end gap-8">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-black/[0.46]">
                  {t('cards.expiry')}
                </span>
                <span className="text-[16px] leading-none tabular-nums text-ink">{expiry}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-black/[0.46]">CVV</span>
                <span className="text-[16px] leading-none tabular-nums text-ink">{cvv ?? '•••'}</span>
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
